import { useSyncExternalStore } from 'react';
import { api } from './api';
import type { VocabTreeDTO } from '../../shared/vocab-types';

/**
 * Shared Vokabular-Loader (Taxonomie Phase 3, v2-Shape ab Phase A). Lädt den
 * `GET /api/vocab`-Baum für Sidebar (dynamische Kategorien), FilterBar +
 * ActiveFilters.
 *
 * **Shared-Module-Store (2026-06-10):** früher fetchte jeder Consumer den Baum
 * eigenständig — auf `/library` sind Sidebar + FilterBar + ActiveFilters
 * gleichzeitig gemountet → 3 parallele identische GETs. Jetzt teilt ein
 * Module-Store eine einzige Quelle: Single-Flight (erster Subscriber lädt, der
 * Rest reused), der Cache überlebt Navigation. Consumer abonnieren via
 * `useSyncExternalStore` (tear-frei). Tri-State-Discriminated-Union konsistent
 * mit `StatsState`/`LoadState` (LibraryPage).
 *
 * **Invalidation:** Vokabular ändert sich nur über `VocabAdminPage`
 * (`/settings/vocab`). Nach jeder Mutation ruft die Page `invalidateVocab()` →
 * der Store refetcht, wenn ein Consumer live ist (z.B. die immer gemountete
 * Sidebar), sonst wird der Cache verworfen und beim nächsten Mount frisch
 * geladen. Cross-Tab-/Fremd-Edits bleiben bis zum nächsten Full-Reload stale —
 * bei 3–5 internen Admin-Usern bewusst akzeptiert (kein Polling/Realtime).
 */
export type VocabState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; vocab: VocabTreeDTO };

const INITIAL: VocabState = { status: 'loading' };

let store: VocabState = INITIAL;
let inFlight: AbortController | null = null;
let started = false; // wurde für die aktuelle Cache-Generation schon ein Fetch gestartet?
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function setStore(next: VocabState) {
  store = next;
  emit();
}

function load() {
  inFlight?.abort();
  const ctrl = new AbortController();
  inFlight = ctrl;
  started = true;
  setStore({ status: 'loading' });
  void (async () => {
    try {
      const vocab = await api<VocabTreeDTO>('/api/vocab', { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      inFlight = null;
      setStore({ status: 'ok', vocab });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      inFlight = null;
      setStore({ status: 'error' });
    }
  })();
}

function ensureLoaded() {
  if (started) return; // bereits geladen oder ladend — reuse statt Doppel-Fetch
  load();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  ensureLoaded();
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): VocabState {
  return store;
}

/**
 * Verwirft den geteilten Cache + refetcht. Nach Vokabular-Mutationen aufrufen,
 * damit live gemountete Consumer (v.a. die Sidebar) die Änderung spiegeln. Ohne
 * Listener wird nur der Cache gedroppt → der nächste Mount lädt frisch.
 */
export function invalidateVocab(): void {
  if (listeners.size === 0) {
    inFlight?.abort();
    inFlight = null;
    started = false;
    store = INITIAL;
    return;
  }
  load();
}

/** Test-only: Module-Store auf den Initialzustand zurücksetzen (Isolation
 *  zwischen `it()`-Blöcken, die den echten Hook fahren). */
export function __resetVocabForTest(): void {
  inFlight?.abort();
  inFlight = null;
  started = false;
  store = INITIAL;
  listeners.clear();
}

export function useVocab(): { state: VocabState; refetch: () => void } {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { state, refetch: load };
}
